import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { branch, compose, lifecycle, renderComponent, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { pendingCpdActivityMap, routes } from '../../../../constants';
import { fullName, isBusinessAdmin, isTeamManager } from '../../../../services';
import { cpdCategorysActions } from '../../../../state/modules/areas-of-advice';
import { businessActions, businessCurrentTeamMembersSelector, businessSelector, fullTeamPendingExternalCpdSelector, specificTeamPendingExternalCpdSelector, teamMembersSelector } from '../../../../state/modules/business';
import { currentUserSelector } from '../../../../state/modules/user';
import { cpdPointActions } from '../../../../state/modules/cpd-points';
import { publisherActions } from '../../../../state/modules/publisher';
import { currentUserCpdDaysRemaining, userAfslSelector } from '../../../../state/modules/user';
import { Icon } from '../../../components';
import Loading from '../../loading';
import { CpdActivityTable } from '../professional-profile/components';

const Completed = () => (
  <div id='section--add-content-questions'>
    <Grid>
      <h1 className='text-center'>&nbsp;</h1>
      <Row className='md-gutter'>
        <Col xs={12} md={6} mdOffset={3} className='boxed'>
          <div className='complete'>
            <Icon icon='caddie-the-dog' />
            <div className='h1'>No pending External CPD to review</div>
            <p>You're all done!</p>
          </div>
        </Col>
      </Row>
    </Grid>
  </div>
);

const CpdActivity = ({
  teamPendingExternalCpdWithNames,
  teamId
}) => (
    <div className='base-layout'>
      <section id='header--profile' key='cpd-activity-header' className='layout--header'>
        <Grid>
          <Row className='sm-gutter'>
            <Col xs={12}>
              <Breadcrumb>
                <LinkContainer to={routes.app.teams.root}>
                  <BreadcrumbItem>
                    Teams
                  </BreadcrumbItem>
                </LinkContainer>
                {
                  teamId && (
                    <LinkContainer to={routes.app.teams.teamMembers(teamId)}>
                      <BreadcrumbItem>
                        Team
                      </BreadcrumbItem>
                    </LinkContainer>
                  )
                }
                <BreadcrumbItem active>
                  External CPD for review
                </BreadcrumbItem>
              </Breadcrumb>
            </Col>
          </Row>
          <Row className='button-gutter'>
            <Col xs={12}>
              <h1>
                <Icon icon='cpd-activity-large' /> External CPD for review
              </h1>
              <hr />
            </Col>
            <Col xs={6} md={2} mdOffset={3}>
              {false ? // disable feature for release
                <LinkContainer
                  to={routes.app.externalCpd.details}
                  className='large full'>
                  <Button>
                    Add External CPD <Icon icon='add-large' />
                  </Button>
                </LinkContainer> : null
              }
            </Col>
          </Row>
        </Grid>
      </section>
      <div id='section--cpd-activity' key='cpd-activity-section' className='layout--section'>
        <Grid>
          <CpdActivityTable
            className='table--cpd-review'
            cpdActivity={teamPendingExternalCpdWithNames}
            cpdActivityMap={pendingCpdActivityMap} />
        </Grid>
      </div>
    </div>
  );

const mapStateToProps = ({ cpdCategorys, publisher, ...state }) => ({
  user: currentUserSelector(state),
  business: businessSelector(state),
  cpdCategorys: cpdCategorys.cpdCategorys,
  publishers: publisher.publishers,
  isFetchingCpdCategorys: cpdCategorys.isFetching,
  isFetchingPublishers: publisher.isFetching,
  fullTeamPendingExternalCpd: fullTeamPendingExternalCpdSelector(state),
  specificTeamPendingExternalCpd: specificTeamPendingExternalCpdSelector(state),
  teamMembers: teamMembersSelector(state),
  specificTeamMembers: fp.get('teamMembers')(businessCurrentTeamMembersSelector(state)),
  isFetchingPendingExternalCpd: state.business.isFetchingPendingExternalCpd,
  afsLicence: userAfslSelector(state),
  cpdDaysRemaining: currentUserCpdDaysRemaining(state)
});

const mapDispatchToProps = (dispatch) => ({
  getCpdCategorys: bindActionCreators(cpdCategorysActions.cpdCategorys.get, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
  getCpdPoints: bindActionCreators(cpdPointActions.cpdPoints.get, dispatch),
  getFullTeamPendingExternalCpd: bindActionCreators(businessActions.business.getFullTeamPendingExternalCpd, dispatch),
  getSpecificTeamPendingExternalCpd: bindActionCreators(businessActions.business.getSpecificTeamPendingExternalCpd, dispatch),
  loadMoreCpdPoints: bindActionCreators(cpdPointActions.cpdPoints.loadMore, dispatch),
  getTeamMembers: bindActionCreators(businessActions.business.team.getTeamMembers, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ match: { params } }) => ({
      teamId: params.teamId,
      uid: params.userId
    })
  ),
  withProps(({ business, user }) => ({
    isTeamManager: !isBusinessAdmin(user) && isTeamManager(user, business),
  })),
  withProps(
    ({ teamId, uid, user, fullTeamPendingExternalCpd, specificTeamPendingExternalCpd, isFetchingPendingExternalCpd, isTeamManager }) => {
      const getPendingExternalCpd = () => {
        if (!teamId) {
          return fullTeamPendingExternalCpd;
        }

        const isTeamManagerWithUserId = uid && isTeamManager;
        const isNotTeamManagerWithUserId = uid && !isTeamManager;
        const isTeamManagerWithoutUserId = !uid && isTeamManager;
        if (isTeamManagerWithUserId) {
          return fp.flow(
            fp.filter(({ userId }) => userId === parseInt(uid, 10)),
            fp.filter(({ userId }) => userId !== user.id)
          )(specificTeamPendingExternalCpd);
        } else if (isNotTeamManagerWithUserId) {
          return fp.filter(({ userId }) => userId === parseInt(uid, 10))(specificTeamPendingExternalCpd);
        } else if (isTeamManagerWithoutUserId) {
          return fp.filter(({ userId }) => userId !== user.id)(specificTeamPendingExternalCpd);
        }

        return specificTeamPendingExternalCpd;
      };
      return ({
        pendingCpd: isFetchingPendingExternalCpd ? null : getPendingExternalCpd()
      });
    }
  ),
  withProps(({
    pendingCpd,
    teamMembers,
    specificTeamMembers,
    teamId
  }) => ({
    teamPendingExternalCpdWithNames: fp.map(
      (externalCpd) => ({
        ...externalCpd,
        userName: fp.head(fp.map(
          t => fullName(t.user)
        )(fp.filter(t => t.user.id === externalCpd.userId)(teamId ? specificTeamMembers : teamMembers)))
      })
    )(fp.sortBy(
      (externalCpd) => externalCpd.timeEarned
    )(
      pendingCpd
    ))
  })),
  withStateHandlers(
    ({
      filter: {
        skip: 0,
        fromDate: undefined,
        toDate: undefined,
        sort: ''
      }
    }),
    {
      onQuerying: () => (filter) => ({ filter })
    }
  ),
  branch(
    ({ business }) => !business,
    renderComponent(Loading)
  ),
  lifecycle({
    componentDidMount() {
      const {
        business,
        isFetchingPendingExternalCpd,
        getFullTeamPendingExternalCpd,
        getSpecificTeamPendingExternalCpd,
        cpdCategorys,
        isFetchingCpdCategorys,
        getCpdCategorys,
        publishers,
        isFetchingPublishers,
        getPublishers,
        getCpdPoints,
        teamId,
        getTeamMembers
      } = this.props;

      if (teamId) {
        getTeamMembers(business.id, teamId, { teamId: teamId })
      }

      if (!cpdCategorys.length && !isFetchingCpdCategorys) {
        getCpdCategorys();
      }

      if (!publishers.length && !isFetchingPublishers) {
        getPublishers();
      }

      if (business && !isFetchingPendingExternalCpd) {
        if (teamId) {
          getSpecificTeamPendingExternalCpd(business.id, teamId)
        } else {
          getFullTeamPendingExternalCpd(business.id);
        }
      }

      getCpdPoints({ skip: 0 });
    }
  }),
  branch(
    ({ isFetchingPendingExternalCpd, teamPendingExternalCpdWithNames }) =>
      !isFetchingPendingExternalCpd &&
      teamPendingExternalCpdWithNames &&
      teamPendingExternalCpdWithNames.length === 0,
    renderComponent(() => (
      <Completed />
    ))
  ),
  branch(
    ({ teamId, specificTeamMembers }) => teamId && !specificTeamMembers,
    renderComponent(Loading)
  )
)(CpdActivity);



// WEBPACK FOOTER //
// ./src/containers/pages/app/external-cpd/review.js